'use client';

import { useTranslation } from 'next-i18next';
import {
  UserCircleIcon,
  PlusCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import {
  TailwindSpinner, Anchor, MissionControlSwitch, LinkStyled,
} from '@components/common';
import { SidebarAccountRow } from '@components/layout/SidebarAccountRow';
import { SidebarContextSwitch } from '@components/layout/SidebarContextSwitch';
import { useSwrMcMyJobsSearch } from '@hooks/useSwrApi/endpoints/my-jobs/useSwrMcMyJobsSearch';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { getI18nLinkHref } from '@services/i18nPaths';
import { MemoizedCompactJobCard } from './CompactJobCard';

const JOBS_PER_PAGE = 5;

interface CompanyJobsSidebarProps {
  locale: Locale;
  selectedJobSlug?: string;
  onJobSelect?: (job: ApiSchema<'McMyJobCard'>) => void;
  /** When provided, tag clicks call this instead of navigating to the standalone applicant page. */
  onTagClick?: (job: ApiSchema<'McMyJobCard'>, status: number) => void;
  className?: string;
  isAuthenticated?: boolean;
  showAccountSettings?: boolean;
  onSwitchContext?: () => void;
}

/**
 * @description Inner component that fetches and displays jobs.
 * Only rendered when user is authenticated.
 */
const CompanyJobsSidebarContent = (props: Omit<CompanyJobsSidebarProps, 'isAuthenticated'>) => {
  const {
    locale, selectedJobSlug, onJobSelect, onTagClick, className, showAccountSettings = true, onSwitchContext,
  } = props;
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();

  const {
    data,
    isLoading,
    isEmpty,
  } = useSwrMcMyJobsSearch({
    locale,
    startIndex: 1,
    limit: JOBS_PER_PAGE,
    urlParams: '&sort=new',
  });

  // Flatten all jobs from paginated data
  const allJobs = data?.flatMap((page) => page?.items || []) || [];

  return (
    <div className={`flex h-full flex-col bg-surface ${className}`}>
      {/* Top content - scrollable */}
      <div className="scrollbar-hide flex-1 overflow-y-auto px-4 pb-4 pt-0">
        {/* Navigation Header - Company Links */}
        <div className="mb-6 flex flex-col gap-3 border-b border-input pb-4">
          {/* New Project - Action TBD */}
          <button
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined' && window.innerWidth < 1024) {
                window.dispatchEvent(new Event('close-sidebar'));
                window.dispatchEvent(new Event('close-detail-modal'));
              }
              void router.push('/mc/job-add/edit');
            }}
            className="flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-150"
            data-test-id="sidebar-new-project"
          >
            <PlusCircleIcon className="size-5 text-ink" />
            <span>{t('header.label.new.job') || 'New job'}</span>
          </button>
        </div>

        {/* Company Jobs Section */}
        {!isEmpty && !isLoading && (
          <div className="mb-2 flex items-center justify-between px-3">
            <h3 className="text-sm font-bold text-ink">
              {t('my-jobs.page.title') || 'My Jobs'}
            </h3>
          </div>
        )}

        {/* Content */}
        <div className="flex flex-col pb-4">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <TailwindSpinner className="size-6 text-primary" />
            </div>
          )}

          {/* Jobs list */}
          {!isEmpty && (
            <div className="flex flex-col gap-1">
              {allJobs.map((job) => (
                <MemoizedCompactJobCard
                  key={job.id}
                  job={job}
                  locale={locale}
                  isSelected={job.slug === selectedJobSlug}
                  onSelect={onJobSelect}
                  onTagClick={onTagClick}
                  buildTagHref={(slug) => getI18nLinkHref(`/mc/job/${slug}/applicants/flow`, locale)}
                />
              ))}
            </div>
          )}

          {/* See all jobs link */}
          {!isEmpty && !isLoading && (
            <div className="mt-6 flex justify-center">
              <LinkStyled
                href="/mc/my-jobs"
                styling="none"
                className="flex items-center gap-1.5 text-base font-semibold text-primary hover:text-primary-light"
              >
                <span>{t('homepage.react.hero.heading.button.all-jobs') || 'My Jobs'}</span>
                <ArrowRightIcon className="size-4" />
              </LinkStyled>
            </div>
          )}
        </div>
      </div>

      {/* Always-visible context toggle (Caut job / Angajez); also available in the account popup */}
      {onSwitchContext && (
        <div className="px-2 pb-2">
          <SidebarContextSwitch activeContext="company" onSwitch={onSwitchContext} />
        </div>
      )}

      {showAccountSettings && <SidebarAccountRow />}
    </div>
  );
};

/**
 * @description Modular company jobs list sidebar component.
 * Displays company jobs with "See more" pagination (10 jobs per page).
 * Shows a sign-in prompt when user is not authenticated.
 * Can be included in different pages like CV detail or hire landing.
 */
export const CompanyJobsSidebar = (props: CompanyJobsSidebarProps) => {
  const { isAuthenticated = false, ...restProps } = props;
  const { t } = useTranslation('common');

  // Show authenticated content if user is logged in
  if (isAuthenticated) {
    return <CompanyJobsSidebarContent {...restProps} />;
  }

  // Show sign-in prompt if user is not authenticated
  return (
    <div className={`flex h-full flex-col bg-surface ${restProps.className || ''}`}>
      <div className="scrollbar-hide flex-1 overflow-y-auto px-4 pt-4">
        {/* Context switch */}
        <div className="mb-6 flex flex-col gap-3 border-b border-input pb-4">
          <MissionControlSwitch
            activeContext="company"
            hireLabel={t('mc.switch.hire')}
            jobSearchLabel={t('mc.switch.job.search')}
            onSwitch={restProps.onSwitchContext ?? (() => {})}
          />
        </div>
      </div>

      {/* Bottom: sign-in row — mirrors SidebarAccountRow's position and height
          (avatar size-xs = 32px) for authenticated users. */}
      <Anchor
        href={`${getI18nLinkHref('/login', restProps.locale)}?_target_path=${encodeURIComponent(getI18nLinkHref('/mc', restProps.locale))}`}
        styling="none"
        className="m-2 flex shrink-0 items-center gap-3 rounded-2xl px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-150"
      >
        <UserCircleIcon className="size-8 shrink-0 text-ink" />
        <span>{t('menu.sign_in-register') || 'Sign In'}</span>
      </Anchor>
    </div>
  );
};
